import React from 'react'

import { Link, useHistory } from 'react-router-dom'
import { RHFInput } from 'react-hook-form-input'
import useForm from 'react-hook-form'

import { Box, Typography, Button } from '@material-ui/core'

import { Dialog, Animation } from '@yapay/react-components'

import { routes } from 'routes'
import {
  Footer,
  PaymentMethodCheckbox,
  CardConfiguration,
  CreditCardCheckbox,
} from './components'
import useSnackbar from 'hooks/useSnackbar'
import * as service from 'service'
import constants from './../../../../constants'
import PaymentMethodsContext from 'contexts/PaymentMethodsContext'
import transfer from 'images/paymentMethodsConfiguration/transfer.png'
import creditCard from 'images/paymentMethodsConfiguration/creditCard.png'

const PaymentMethodsConfiguration = () => {
  const history = useHistory()
  const context = React.useContext(PaymentMethodsContext)
  const snackbar = useSnackbar()
  const [isLoading, setIsLoading] = React.useState(false)
  const [open, setOpen] = React.useState(false)
  const { register, setValue, handleSubmit } = useForm({
    defaultValues: { ...context.data.configuration },
  })

  const handleClose = () => {
    setOpen(false)
    history.push(routes.settings.paymentMethods.edit)
  }

  const onSubmit = async dataForm => {
    try {
      setIsLoading(true)

      context.setData(data => ({
        ...data,
        configuration: { ...data.configuration, ...dataForm },
      }))

      const billetMethods = context.data.selectMethods.billet
        ? constants.paymentMethods.BILLETS_CONFIG_GATEWAY.map(
            method => method.paymentMethod,
          )
        : []

      const creditCardMethods = Object.keys(dataForm.creditCard).filter(
        method => dataForm.creditCard[method],
      )

      const transferMethods = Object.keys(dataForm.transfer).filter(
        method => dataForm.transfer[method],
      )

      await Promise.all([
        service.recurrence.processors.update({
          processor: 'yapay',
          methods: [...billetMethods, ...creditCardMethods, ...transferMethods],
          tags: [
            {
              tag: 'token_account',
              value: context.data.integrationToken.integrationToken,
            },
          ],
        }),
        service.recurrence.settings.update({
          settingId: constants.settings.YAPAY_TOKEN_ACCOUNT,
          value: context.data.integrationToken.integrationToken,
        }),
        service.recurrence.settings.update({
          settingId: constants.settings.BILLET_METHOD,
          value: context.data.selectMethods.billet ? 'yapay' : '',
        }),
        service.recurrence.settings.update({
          settingId: constants.settings.CARD_METHOD,
          value: context.data.selectMethods.creditCard ? 'yapay' : '',
        }),
        service.recurrence.settings.update({
          settingId: constants.settings.TRANSFER_METHOD,
          value: context.data.selectMethods.transfer ? 'yapay' : '',
        }),
        service.recurrence.settings.update({
          settingId: constants.settings.BALANCE_METHOD,
          value: context.data.selectMethods.balance ? 'yapay' : '',
        }),
      ])

      setOpen(true)
      setIsLoading(false)
    } catch (error) {
      setIsLoading(false)
      snackbar.open({
        message: `Erro ao configurar meios de pagamento. ${error?.response?.data?.message}`,
        variant: 'error',
      })
    }
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Box mb={2}>
        <Typography color="textSecondary">
          Configure a(s) forma(s) de pagamento
        </Typography>
      </Box>

      <CardConfiguration image={transfer} title="Transferência bancária">
        <Box mb={2}>
          <Typography color="textSecondary">
            Para qual(is) banco(s) seu cliente poderá fazer as transferências?
            <br />
            Você pode escolher mais de um.
          </Typography>
        </Box>

        <Box
          display="grid"
          gridTemplateColumns={{
            xs: '1fr 1fr',
            sm: 'repeat(auto-fill, 130px)',
          }}
          gridGap={4}
          mb={4}
        >
          {constants.paymentMethods.TRANSFERS_CONFIG_GATEWAY.map(
            paymentMethod => (
              <RHFInput
                as={
                  <PaymentMethodCheckbox
                    id={paymentMethod.paymentMethod}
                    image={paymentMethod.image}
                    imageAlt={paymentMethod.name}
                    description={paymentMethod.name.replace(
                      'Transferência ',
                      '',
                    )}
                  />
                }
                name={`transfer.${paymentMethod.paymentMethod}`}
                type="checkbox"
                register={register}
                setValue={setValue}
                key={paymentMethod.paymentMethod}
              />
            ),
          )}
        </Box>

        <Typography color="textSecondary">
          Observação: as transferências serão creditadas em sua conta Yapay.
        </Typography>
      </CardConfiguration>

      <CardConfiguration image={creditCard} title="Cartão de crédito">
        <Box mb={2}>
          <Typography color="textSecondary">
            Selecione as bandeiras que deseja aceitar.
          </Typography>
        </Box>

        <Box
          display="grid"
          gridTemplateColumns={{
            xs: '1fr 1fr',
            md: '1fr 1fr 1fr',
          }}
          gridColumnGap={4}
          gridRowGap={12}
          mb={4}
        >
          {constants.paymentMethods.CREDIT_CARDS_GATEWAY.map(paymentMethod => (
            <RHFInput
              as={
                <CreditCardCheckbox
                  id={paymentMethod.paymentMethod}
                  image={paymentMethod.image}
                  description={paymentMethod.name}
                />
              }
              name={`creditCard.${paymentMethod.paymentMethod}`}
              type="checkbox"
              register={register}
              setValue={setValue}
              key={paymentMethod.paymentMethod}
            />
          ))}
        </Box>
      </CardConfiguration>

      <Dialog.Dialog open={open} onClose={handleClose}>
        <Dialog.DialogTitleWithClose onClose={handleClose} />
        <Dialog.DialogContentAnimation>
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
          >
            <Animation
              animation="success"
              size="normal"
              description="Meios de pagamento configurados!"
            />

            <Box width={140} mt={6}>
              <Button
                variant="contained"
                color="primary"
                size="large"
                onClick={handleClose}
                fullWidth
              >
                OK
              </Button>
            </Box>
          </Box>
        </Dialog.DialogContentAnimation>
      </Dialog.Dialog>

      <Footer
        prevButtonProps={{
          component: Link,
          to: routes.settings.paymentMethods.steps.selectMethods,
        }}
        nextButtonText={isLoading ? 'Carregando...' : 'Continuar'}
        nextButtonProps={{ type: 'submit', disabled: isLoading }}
      />
    </form>
  )
}

export default PaymentMethodsConfiguration
